import { appConfig } from "../config/appConfig.js";

function getApiBaseUrl() {
  const raw = appConfig.communityApiUrl;
  if (!raw) return null;
  return raw.replace(/\/$/, "");
}

export function isCommunityApiConfigured() {
  return !!getApiBaseUrl();
}

/**
 * @param {string} path
 * @param {RequestInit & { token?: string }} options
 */
async function jsonFetch(path, options = {}) {
  const base = getApiBaseUrl();
  if (!base) {
    throw new Error("Community API is not configured");
  }

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  if (options.token) {
    headers.Authorization = `Bearer ${options.token}`;
    headers["x-community-token"] = options.token;
  }

  const { token: _t, headers: _h, ...rest } = options;
  const response = await fetch(`${base}${path}`, { ...rest, headers });
  const text = await response.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* ignore */
  }
  if (!response.ok) {
    const msg = data?.error || data?.message || text || `HTTP ${response.status}`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }
  return data;
}

/**
 * Claim a unique nickname and receive a session token.
 * @param {string} userId
 * @returns {Promise<{ userId: string, joinedAt: string, token: string }>}
 */
export async function joinCommunityRemote(userId) {
  return jsonFetch("/community/join", {
    method: "POST",
    body: JSON.stringify({ userId }),
  });
}

/** @deprecated prefer joinCommunityRemote */
export async function registerCommunityProfile(userId) {
  return joinCommunityRemote(userId);
}

/**
 * @returns {Promise<import('../storage/communityStorage.js').CommunityPost[]>}
 */
export async function fetchCommunityFeed(limit = 50) {
  const data = await jsonFetch(`/community/feed?limit=${encodeURIComponent(String(limit))}`, {
    method: "GET",
  });
  return Array.isArray(data?.posts) ? data.posts : [];
}

/**
 * @param {string} token
 * @param {string} category
 * @param {string} body
 */
export async function createRemoteCommunityPost(token, category, body) {
  return jsonFetch("/community/posts", {
    method: "POST",
    body: JSON.stringify({ category, body }),
    token,
  });
}

/**
 * @param {string} postId
 * @param {string} token
 * @param {string} text
 */
export async function addRemoteEncouragement(postId, token, text) {
  return jsonFetch(`/community/posts/${encodeURIComponent(postId)}/encouragements`, {
    method: "POST",
    body: JSON.stringify({ text }),
    token,
  });
}

/**
 * @param {string} postId
 * @param {string} token
 */
export async function deleteRemoteCommunityPost(postId, token) {
  return jsonFetch(`/community/posts/${encodeURIComponent(postId)}`, {
    method: "DELETE",
    token,
  });
}

/**
 * @param {string} postId
 * @param {string} token
 * @param {string} [reason]
 */
export async function reportRemoteCommunityPost(postId, token, reason = "") {
  return jsonFetch(`/community/posts/${encodeURIComponent(postId)}/report`, {
    method: "POST",
    body: JSON.stringify({ reason }),
    token,
  });
}
